import React, { useState, useEffect } from 'react';
import {
  Save,
  TestTube,
  CheckCircle,
  AlertCircle,
  Eye,
  EyeOff,
  Key,
  Video,
  Link
} from 'lucide-react';
import { fathomService } from '../../services/fathomService';
import { FathomConfig } from '../../types/fathom';
import { recordingProviderService } from '../../services/recordingProviderService';
import { supabase } from '../../services/supabaseService';

const FathomSettings: React.FC = () => {
  const [config, setConfig] = useState<FathomConfig>({
    apiKey: '',
    webhookSecret: '',
    isConfigured: false,
    webhookUrl: 'https://crm.ceroideas.es/api/fathom/webhook'
  });

  const [isSaving, setIsSaving] = useState(false);
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);
  const [showApiKey, setShowApiKey] = useState(false);
  const [showWebhookSecret, setShowWebhookSecret] = useState(false);
  const [isEnabled, setIsEnabled] = useState(false);
  const [isLoadingSettings, setIsLoadingSettings] = useState(true);

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      console.log('🔄 Loading Fathom configuration from database...');
      setIsLoadingSettings(true);

      // Load from database instead of localStorage
      const savedConfig = await fathomService.loadConfigFromDatabase();

      if (savedConfig) {
        setConfig(savedConfig);
        console.log('✅ Fathom configuration loaded from database');
      } else {
        console.log('ℹ️ No Fathom configuration found in database');
      }

      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const enabled = await recordingProviderService.isFathomEnabled(user.id);
        setIsEnabled(enabled);
        console.log('✅ Fathom enabled status:', enabled);
      }
    } catch (error) {
      console.error('❌ Error loading Fathom configuration:', error);
    } finally {
      setIsLoadingSettings(false);
    }
  };

  const handleToggleEnabled = async (enabled: boolean) => {
    try {
      // Si intenta habilitar sin configurar, mostrar advertencia
      if (enabled && !config.isConfigured) {
        alert('⚠️ Por favor configura primero tu API Key de Fathom y presiona "Guardar Configuración" antes de habilitar la integración.');
        return;
      }

      console.log(`🎬 ${enabled ? 'Enabling' : 'Disabling'} Fathom...`);

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        alert('❌ Error: Usuario no autenticado');
        return;
      }

      await recordingProviderService.toggleFathom(user.id, enabled);
      setIsEnabled(enabled);
      console.log(`✅ Fathom ${enabled ? 'enabled' : 'disabled'}`);
    } catch (error) {
      console.error('❌ Error toggling Fathom:', error);
      alert('❌ Error al cambiar el estado de Fathom');
    }
  };

  const handleSave = async () => {
    if (!config.apiKey || config.apiKey.trim().length < 10) {
      alert('❌ API Key muy corta o inválida');
      return;
    }

    setIsSaving(true);
    try {
      console.log('💾 Guardando configuración Fathom en base de datos...');
      const updatedConfig = {
        ...config,
        isConfigured: true
      };

      // Save to database using async method
      await fathomService.saveConfigAsync(updatedConfig);
      setConfig(updatedConfig);

      setTestResult({ success: true, message: '✅ Configuración guardada en base de datos. Usa el botón "Test" para verificar la conexión.' });
      alert('✅ Configuración de Fathom guardada correctamente en base de datos. Ahora puedes habilitar la integración.');
    } catch (error: any) {
      console.error('Error saving Fathom config:', error);
      const errorMessage = error?.message || 'Error desconocido';
      alert('❌ Error al guardar la configuración: ' + errorMessage);
    } finally {
      setIsSaving(false);
    }
  };

  const handleTest = async () => {
    setTestResult(null);

    if (!config.apiKey || config.apiKey.trim().length < 10) {
      setTestResult({ success: false, message: '❌ API Key muy corta o inválida' });
      return;
    }

    try {
      fathomService.saveConfig(config);

      console.log('🧪 Testing Fathom connection...');
      const isValid = await fathomService.testConnection();

      if (isValid) {
        setTestResult({ success: true, message: '✅ Conexión exitosa con Fathom - API Key válida y funcionando correctamente' });
        setConfig(prev => ({ ...prev, isConfigured: true }));
        console.log('✅ Fathom test successful');
      } else {
        setTestResult({ success: false, message: '❌ La conexión falló - verifica tu API Key' });
        setConfig(prev => ({ ...prev, isConfigured: false }));
        console.log('❌ Fathom test failed');
      }
    } catch (error: any) {
      let errorMessage = error?.message || 'Error desconocido';

      if (errorMessage.includes('quota limit reached')) {
        errorMessage = 'Has alcanzado el límite de tu plan de Fathom. La API está pausada temporalmente. Por favor espera o actualiza tu plan.';
      } else if (errorMessage.includes('Invalid credentials') || errorMessage.includes('401')) {
        errorMessage = 'Credenciales inválidas. Verifica tu API Key de Fathom.';
      } else if (errorMessage.includes('permissions') || errorMessage.includes('403')) {
        errorMessage = 'API Key sin permisos suficientes.';
      } else if (errorMessage.includes('not configured')) {
        errorMessage = 'Fathom no está configurado. Ingresa tu API Key primero.';
      } else if (errorMessage.includes('404')) {
        errorMessage = 'Endpoint no encontrado. Verifica que la API de Fathom esté disponible.';
      } else if (errorMessage.includes('timeout')) {
        errorMessage = 'Timeout: Fathom no responde. Verifica tu conexión a internet.';
      } else if (errorMessage.includes('fetch') || errorMessage.includes('network')) {
        errorMessage = 'Error de red. Verifica tu conexión a internet.';
      }

      setTestResult({ success: false, message: `❌ ${errorMessage}` });
      setConfig(prev => ({ ...prev, isConfigured: false }));
      console.error('❌ Fathom test error:', error);
    }
  };

  if (isLoadingSettings) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Enable/Disable Toggle */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-gray-900">Habilitar Fathom</h3>
            <p className="text-sm text-gray-600 mt-1">
              {!config.isConfigured
                ? 'Configura primero tu API Key abajo y luego activa la integración'
                : 'Activa o desactiva la integración con Fathom para grabación de reuniones'
              }
            </p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={isEnabled}
              onChange={(e) => handleToggleEnabled(e.target.checked)}
              className="sr-only peer"
            />
            <div className="w-14 h-7 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[4px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-blue-600"></div>
          </label>
        </div>
        {isEnabled && (
          <div className="mt-4 flex items-center space-x-2 text-sm text-green-700 bg-green-50 px-3 py-2 rounded-lg">
            <CheckCircle className="w-4 h-4" />
            <span>Fathom está habilitado</span>
          </div>
        )}
        {!isEnabled && (
          <div className="mt-4 flex items-center space-x-2 text-sm text-gray-600 bg-gray-50 px-3 py-2 rounded-lg">
            <AlertCircle className="w-4 h-4" />
            <span>Fathom está deshabilitado</span>
          </div>
        )}
      </div>

      {/* Configuration Banner - Show when not configured */}
      {!config.isConfigured && isEnabled && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
          <div className="flex items-center space-x-3">
            <div className="flex-shrink-0">
              <Link className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <h3 className="text-base font-medium text-blue-900">Configurar Fathom</h3>
              <p className="text-sm text-blue-700 mt-1">
                Configura tu API Key de Fathom para comenzar
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Connection Status */}
      <div className={`rounded-lg p-4 ${
        config.isConfigured && testResult?.success ? 'bg-green-50 border border-green-200' :
        testResult?.success === false ? 'bg-red-50 border border-red-200' :
        'bg-blue-50 border border-blue-200'
      }`}>
        <div className="flex items-center space-x-3">
          {config.isConfigured && testResult?.success ? (
            <CheckCircle className="w-5 h-5 text-green-600" />
          ) : testResult?.success === false ? (
            <AlertCircle className="w-5 h-5 text-red-600" />
          ) : (
            <Key className="w-5 h-5 text-blue-600" />
          )}

          <div>
            <h3 className={`font-medium ${
              config.isConfigured && testResult?.success ? 'text-green-900' :
              testResult?.success === false ? 'text-red-900' :
              'text-blue-900'
            }`}>
              {config.isConfigured && testResult?.success ? 'Fathom Conectado' :
               testResult?.success === false ? 'Error de Conexión' :
               'Configurar Fathom'}
            </h3>
            <p className={`text-sm ${
              config.isConfigured && testResult?.success ? 'text-green-700' :
              testResult?.success === false ? 'text-red-700' :
              'text-blue-700'
            }`}>
              {testResult?.message || 'Configura tu API Key de Fathom para comenzar'}
            </p>
          </div>
        </div>
      </div>

      {/* API Configuration */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Configuración de API</h2>
          <p className="text-sm text-gray-600 mt-1">
            Configura tu API Key de Fathom para habilitar grabación y análisis automático
          </p>
        </div>

        <div className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              API Key de Fathom *
            </label>
            <p className="text-xs text-gray-500 mb-2">
              Obtén tu API Key desde el dashboard de Fathom
            </p>
            <div className="relative">
              <input
                type={showApiKey ? 'text' : 'password'}
                value={config.apiKey}
                onChange={(e) => setConfig({ ...config, apiKey: e.target.value.trim() })}
                className="w-full px-3 py-2 pr-20 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="fathom_..."
              />
              <div className="absolute right-2 top-1/2 transform -translate-y-1/2 flex items-center space-x-1">
                <button
                  type="button"
                  onClick={() => setShowApiKey(!showApiKey)}
                  className="p-1 text-gray-400 hover:text-gray-600"
                >
                  {showApiKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
                <button
                  onClick={handleTest}
                  disabled={!config.apiKey}
                  className="flex items-center space-x-1 px-2 py-1 bg-blue-600 text-white rounded text-xs hover:bg-blue-700 disabled:opacity-50 transition-colors"
                >
                  <TestTube className="w-3 h-3" />
                  <span>Test</span>
                </button>
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Encuentra tu API Key en Fathom → Settings → Integrations → API
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Webhook Secret
            </label>
            <p className="text-xs text-gray-500 mb-2">
              Secret para validar los webhooks de Fathom (opcional pero recomendado)
            </p>
            <div className="relative">
              <input
                type={showWebhookSecret ? 'text' : 'password'}
                value={config.webhookSecret || ''}
                onChange={(e) => setConfig({ ...config, webhookSecret: e.target.value.trim() })}
                className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="whsec_..."
              />
              <button
                type="button"
                onClick={() => setShowWebhookSecret(!showWebhookSecret)}
                className="absolute right-2 top-1/2 transform -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600"
              >
                {showWebhookSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Encuentra el Webhook Secret en la misma sección de API en Fathom
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              URL del Webhook
            </label>
            <input
              type="url"
              value={config.webhookUrl}
              onChange={(e) => setConfig({ ...config, webhookUrl: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="https://crm.ceroideas.es/api/fathom/webhook"
            />
            <p className="text-xs text-gray-500 mt-1">
              URL donde Fathom enviará los eventos de reuniones
            </p>
          </div>

          <div className="flex justify-end">
            <button
              onClick={handleSave}
              disabled={isSaving || !config.apiKey}
              className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isSaving ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              ) : (
                <Save className="w-4 h-4" />
              )}
              <span>{isSaving ? 'Guardando...' : 'Guardar Configuración'}</span>
            </button>
          </div>
        </div>
      </div>

      {/* How to get API Key */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
        <div className="flex items-start space-x-3">
          <Video className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
          <div className="flex-1">
            <h4 className="text-sm font-medium text-blue-900">¿Cómo obtener tu API Key y Webhook Secret de Fathom?</h4>
            <ol className="text-sm text-blue-700 mt-2 space-y-1 list-decimal list-inside">
              <li>Ve a <a href="https://app.fathom.video" target="_blank" rel="noopener noreferrer" className="underline">app.fathom.video</a></li>
              <li>Inicia sesión en tu cuenta de Fathom</li>
              <li>Ve a Settings → Integrations</li>
              <li>Navega a la sección API</li>
              <li>Genera una nueva API key con permisos completos</li>
              <li>Copia la API key generada (empieza con "fathom_...")</li>
              <li>En la misma sección, copia también el Webhook Secret (empieza con "whsec_...")</li>
              <li>Pega ambos valores en los campos correspondientes arriba</li>
            </ol>
            <div className="mt-3 p-3 bg-blue-100 rounded border border-blue-300">
              <p className="text-xs text-blue-800">
                <strong>Funcionalidades:</strong> Grabación automática, transcripción en tiempo real,
                resúmenes con IA, extracción de action items, análisis de sentimiento y insights de participación.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Integration Features */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Funciones de Integración</h2>
        </div>

        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <h3 className="text-md font-medium text-gray-900">Automatizaciones Activas</h3>
              <div className="space-y-3">
                <div className="flex items-center space-x-3">
                  <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                  <span className="text-sm text-gray-700">Nueva reunión → Programar grabación</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                  <span className="text-sm text-gray-700">Reunión termina → Transcripción automática</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                  <span className="text-sm text-gray-700">Transcripción lista → Resumen con IA</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                  <span className="text-sm text-gray-700">Resumen listo → Actividad en CRM</span>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="text-md font-medium text-gray-900">Análisis Disponibles</h3>
              <div className="space-y-3">
                <div className="flex items-center space-x-3">
                  <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                  <span className="text-sm text-gray-700">Transcripción completa con timestamps</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                  <span className="text-sm text-gray-700">Resumen ejecutivo automático</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                  <span className="text-sm text-gray-700">Extracción de action items</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                  <span className="text-sm text-gray-700">Análisis de sentimiento</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                  <span className="text-sm text-gray-700">Insights de participación</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FathomSettings;
